import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import '../Admin/Users.css';

const RecipeList = forwardRef(({ onSelectRecipe, selectedRecipeId, onNew }, ref) => {
  const { t } = useTranslation('production');
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    refresh: fetchRecipes
  }));

  useEffect(() => {
    fetchRecipes();
  }, [statusFilter]);

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/formulations/recipes?page_size=100');
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setRecipes(data.recipes || []);
      }
    } catch (e) {
      console.error('Error fetching recipes:', e);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecipes = recipes.filter(r => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (r.recipe_code || '').toLowerCase().includes(s) ||
      (r.recipe_name || '').toLowerCase().includes(s) ||
      (r.output_item_name || '').toLowerCase().includes(s)
    );
  });

  const getStatusBadge = (status) => {
    const map = {
      DRAFT: 'status-warning',
      ACTIVE: 'status-success',
      OBSOLETE: 'status-inactive',
    };
    return <span className={`status-badge ${map[status] || 'status-inactive'}`}>{status ? t('recipes.status.' + status, { defaultValue: status }) : status}</span>;
  };

  const formatCurrency = (val) => {
    if (val == null) return '-';
    return `$${parseFloat(val).toFixed(2)}`;
  };

  if (loading && recipes.length === 0) {
    return <div className="list-container"><div className="loading">{t('recipeList.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>🧪 {t('recipeList.title')}</h2>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('recipeList.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchRecipes()}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('recipeList.allStatus')}</option>
            <option value="DRAFT">{t('recipes.status.DRAFT')}</option>
            <option value="ACTIVE">{t('recipes.status.ACTIVE')}</option>
            <option value="OBSOLETE">{t('recipes.status.OBSOLETE')}</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('common:ui.code')}</th>
              <th>{t('common:ui.name')}</th>
              <th>{t('recipeList.columns.output')}</th>
              <th>{t('common:ui.status')}</th>
              <th>{t('recipeList.columns.cost')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecipes.length === 0 ? (
              <tr><td colSpan="5" className="no-data">{t('recipeList.empty')}</td></tr>
            ) : (
              filteredRecipes.map(r => (
                <tr
                  key={r.recipe_id}
                  className={selectedRecipeId === r.recipe_id ? 'selected' : ''}
                  onClick={() => onSelectRecipe(r.recipe_id)}
                >
                  <td><strong>{r.recipe_code}</strong></td>
                  <td>{r.recipe_name}</td>
                  <td>{r.output_item_name || '-'}</td>
                  <td>{getStatusBadge(r.status)}</td>
                  <td>{formatCurrency(r.total_cost)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default RecipeList;
